import { Avatar, Button } from "@cheatsheetz/core-ui";
import { useSession } from "@cheatsheetz/sheetz-accounts/providers/Session/useSession";
import { AccountRoutes } from "@cheatsheetz/sheetz-accounts/routes";
import { CheatsheetRoutes } from "@cheatsheetz/sheetz-cheatsheets/routes";
import { ElementRoutes } from "@cheatsheetz/sheetz-elements/routes";
import { HomeRoutes } from "@cheatsheetz/sheetz-home/routes";
import { WidgetRoutes } from "@cheatsheetz/sheetz-widgets/routes";
import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  BuildingOffice2Icon,
  ChevronDownIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  RectangleStackIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useState } from "react";
import { Menu, MenuItem, MenuTrigger, Popover, Separator } from "react-aria-components";
import { useLocation, useNavigate } from "react-router-dom";
import { NavButton, NavLink } from "./NavItem";
import { ThemeToggle } from "./ThemeToggle";

interface NavigationMenuProps {
  onMobileMenuOpenChange?: (open: boolean) => void;
}

export function NavigationMenu({ onMobileMenuOpenChange }: NavigationMenuProps) {
  const { isAuthenticated, user, entitlements, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, _setMobileMenuOpen] = useState(false);
  const userSubject = entitlements.find((e) => e.type === "USER");
  const userDisplayName =
    userSubject?.name || user?.displayName || user?.userName || "Account";

  const setMobileMenuOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      _setMobileMenuOpen((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        if (next !== prev) onMobileMenuOpenChange?.(next);
        return next;
      });
    },
    [onMobileMenuOpenChange],
  );

  async function handleMenuAction(key: React.Key) {
    if (key === "settings") {
      navigate(AccountRoutes.Account);
    } else if (key === "library") {
      navigate(ElementRoutes.Library);
    } else if (key === "organizations") {
      navigate(AccountRoutes.Organizations);
    } else if (key === "logout") {
      await logout();
      navigate(HomeRoutes.Home);
    }
  }

  function handleAboutAction(key: React.Key) {
    if (key === "about") {
      navigate(HomeRoutes.About);
    } else if (key === "privacy") {
      navigate(HomeRoutes.Privacy);
    } else if (key === "terms") {
      navigate(HomeRoutes.Terms);
    } else if (key === "licenses") {
      navigate(HomeRoutes.Licenses);
    }
  }

  function handleCheetsAction(key: React.Key) {
    if (key === "widgets") {
      navigate(WidgetRoutes.Widgets);
    } else if (key === "cheatsheets") {
      navigate(CheatsheetRoutes.Cheatsheets);
    }
  }

  return (
    <>
      {/* Desktop nav */}
      <nav className="hidden items-center gap-2 md:flex">
        <NavLink to={HomeRoutes.Home}>Home</NavLink>
        <MenuTrigger>
          <Button intent="plain" size="small" className="gap-1 px-3">
            About
            <ChevronDownIcon className="size-3.5" />
          </Button>
          <Popover
            placement="bottom start"
            className="min-w-[10rem] overflow-hidden rounded-lg border border-border bg-overlay text-overlay-fg shadow-lg entering:animate-in entering:fade-in entering:zoom-in-95 exiting:animate-out exiting:fade-out exiting:zoom-out-95"
          >
            <Menu onAction={handleAboutAction} className="p-1 outline-none">
              <MenuItem
                id="about"
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
              >
                About Cheet.io
              </MenuItem>
              <MenuItem
                id="privacy"
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
              >
                Privacy Policy
              </MenuItem>
              <MenuItem
                id="terms"
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
              >
                Terms of Use
              </MenuItem>
              <MenuItem
                id="licenses"
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
              >
                Licenses
              </MenuItem>
            </Menu>
          </Popover>
        </MenuTrigger>
        {isAuthenticated ? (
          <>
            <MenuTrigger>
              <Button intent="plain" size="small" className="gap-1 px-3">
                Cheets
                <ChevronDownIcon className="size-3.5" />
              </Button>
              <Popover
                placement="bottom start"
                className="min-w-[10rem] overflow-hidden rounded-lg border border-border bg-overlay text-overlay-fg shadow-lg entering:animate-in entering:fade-in entering:zoom-in-95 exiting:animate-out exiting:fade-out exiting:zoom-out-95"
              >
                <Menu onAction={handleCheetsAction} className="p-1 outline-none">
                  <MenuItem
                    id="widgets"
                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
                  >
                    Widgets
                  </MenuItem>
                  <MenuItem
                    id="cheatsheets"
                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
                  >
                    Sheets
                  </MenuItem>
                </Menu>
              </Popover>
            </MenuTrigger>
            <MenuTrigger>
              <Button
                intent="plain"
                size="small"
                aria-label="User menu"
                className="gap-1.5 px-3"
              >
                <Avatar avatarUrl={userSubject?.avatarUrl} name={userDisplayName} />
                {userDisplayName}
                <ChevronDownIcon className="size-3.5" />
              </Button>
              <Popover
                placement="bottom end"
                className="min-w-[12rem] overflow-hidden rounded-lg border border-border bg-overlay text-overlay-fg shadow-lg entering:animate-in entering:fade-in entering:zoom-in-95 exiting:animate-out exiting:fade-out exiting:zoom-out-95"
              >
                <Menu onAction={handleMenuAction} className="p-1 outline-none">
                  <MenuItem
                    id="header"
                    isDisabled
                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-fg outline-none"
                  >
                    <Avatar
                      avatarUrl={userSubject?.avatarUrl}
                      name={userDisplayName}
                      size="md"
                    />
                    {userDisplayName}
                  </MenuItem>
                  <Separator className="mx-2 my-1 border-t border-border" />
                  <MenuItem
                    id="settings"
                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
                  >
                    <Cog6ToothIcon className="size-4" />
                    Settings
                  </MenuItem>
                  <MenuItem
                    id="library"
                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
                  >
                    <RectangleStackIcon className="size-4" />
                    Library
                  </MenuItem>
                  <MenuItem
                    id="organizations"
                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none hover:bg-accent focus:bg-accent"
                  >
                    <BuildingOffice2Icon className="size-4" />
                    Organizations
                  </MenuItem>
                  <MenuItem
                    id="logout"
                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-danger outline-none hover:bg-danger/10 focus:bg-danger/10"
                  >
                    <ArrowRightStartOnRectangleIcon className="size-4" />
                    Sign out
                  </MenuItem>
                </Menu>
              </Popover>
            </MenuTrigger>
          </>
        ) : (
          <NavLink
            to={`${AccountRoutes.Login}?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
          >
            Login
          </NavLink>
        )}
        <ThemeToggle />
      </nav>

      {/* Mobile controls */}
      <div className="flex items-center gap-2 md:hidden">
        <ThemeToggle />
        <Button
          intent="plain"
          shape="square"
          size="small"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          onPress={() => setMobileMenuOpen((prev) => !prev)}
        >
          {mobileMenuOpen ? (
            <XMarkIcon className="size-5" />
          ) : (
            <Bars3Icon className="size-5" />
          )}
        </Button>
      </div>

      {/* Mobile menu flyout + backdrop */}
      {mobileMenuOpen && (
        <>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismisses on tap */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismisses on tap */}
          <div
            className="fixed inset-x-0 top-14 bottom-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav className="absolute inset-x-0 top-14 z-50 border-b border-border bg-navbar px-4 pb-4 pt-2 shadow-lg md:hidden">
            <div className="flex flex-col gap-1">
              <NavLink
                to={HomeRoutes.Home}
                size="medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </NavLink>
              <span className="px-3 py-1 text-xs font-semibold text-muted-fg">About</span>
              <NavLink
                to={HomeRoutes.About}
                size="medium"
                className="px-5"
                onClick={() => setMobileMenuOpen(false)}
              >
                <InformationCircleIcon className="size-4" />
                About Cheet.io
              </NavLink>
              <NavLink
                to={HomeRoutes.Privacy}
                size="medium"
                className="px-5"
                onClick={() => setMobileMenuOpen(false)}
              >
                Privacy Policy
              </NavLink>
              <NavLink
                to={HomeRoutes.Terms}
                size="medium"
                className="px-5"
                onClick={() => setMobileMenuOpen(false)}
              >
                Terms of Use
              </NavLink>
              <NavLink
                to={HomeRoutes.Licenses}
                size="medium"
                className="px-5"
                onClick={() => setMobileMenuOpen(false)}
              >
                Licenses
              </NavLink>
              <div className="mx-3 my-1 border-t border-border" />
              {isAuthenticated ? (
                <>
                  <span className="px-3 py-1 text-xs font-semibold text-muted-fg">
                    Cheets
                  </span>
                  <NavLink
                    to={WidgetRoutes.Widgets}
                    size="medium"
                    className="px-5"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Widgets
                  </NavLink>
                  <NavLink
                    to={CheatsheetRoutes.Cheatsheets}
                    size="medium"
                    className="px-5"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sheets
                  </NavLink>
                  <div className="mx-3 my-1 border-t border-border" />
                  <span className="flex items-center gap-2 px-3 py-1 text-xs font-semibold text-muted-fg">
                    <Avatar avatarUrl={userSubject?.avatarUrl} name={userDisplayName} />
                    {userDisplayName}
                  </span>
                  <NavLink
                    to={AccountRoutes.Account}
                    size="medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Cog6ToothIcon className="size-4" />
                    Settings
                  </NavLink>
                  <NavLink
                    to={ElementRoutes.Library}
                    size="medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <RectangleStackIcon className="size-4" />
                    Library
                  </NavLink>
                  <NavLink
                    to={AccountRoutes.Organizations}
                    size="medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <BuildingOffice2Icon className="size-4" />
                    Organizations
                  </NavLink>
                  <NavButton
                    intent="danger"
                    size="medium"
                    className="text-left"
                    onClick={async () => {
                      setMobileMenuOpen(false);
                      await logout();
                      navigate(HomeRoutes.Home);
                    }}
                  >
                    <ArrowRightStartOnRectangleIcon className="size-4" />
                    Sign out
                  </NavButton>
                </>
              ) : (
                <NavLink
                  to={`${AccountRoutes.Login}?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
                  size="medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </NavLink>
              )}
            </div>
          </nav>
        </>
      )}
    </>
  );
}
